import {
  MdDownload,
  MdOpenInBrowser,
  MdOpenInNew,
  MdSearch,
  MdContentCopy,
  MdContentPaste,
} from "react-icons/md";
import Link from "next/link";
import { FaAmazon, FaAndroid, FaHtml5 } from "react-icons/fa";
import { SiAppletv, SiSamsung } from "react-icons/si";
import { PiTelevisionSimple } from "react-icons/pi";
import { env } from "@src/env.mjs";

export const pairInstructions: Record<ScreenIdType, React.ReactNode[]> = {
  webBrowser: [
    <div
      key="webBrowser_0"
      className="flex h-full min-w-full flex-col items-center justify-center gap-6"
    >
      <CustomIcon>
        <MdContentCopy className="" />
      </CustomIcon>
      <div className="flex flex-col items-center gap-3 text-center">
        <p className="text-lg font-medium text-gray-700 dark:text-gray-200">
          Go to the other tab and
        </p>
        <span className="text-xl font-semibold text-accentColor dark:text-darkAccentColor">
          copy the pairing code
        </span>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          displayed there
        </p>
      </div>
    </div>,
    <div
      key="webBrowser_1"
      className="flex h-full min-w-full flex-col items-center justify-center gap-6"
    >
      <CustomIcon>
        <MdContentPaste className="" />
      </CustomIcon>
      <div className="flex flex-col items-center gap-3 text-center">
        <p className="text-lg font-medium text-gray-700 dark:text-gray-200">
          Come back to this tab and
        </p>
        <span className="text-xl font-semibold text-accentColor dark:text-darkAccentColor">
          paste the code below
        </span>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          to connect your browser screen
        </p>
      </div>
    </div>
  ],
  web: [
    <div
      key="web_0"
      className="flex h-full min-w-full flex-col items-center justify-center gap-2"
    >
      <CustomIcon>
        <MdOpenInBrowser />
      </CustomIcon>
      <div className="flex gap-2">
        {"Go to "}

        <Link
          href={`${env.NEXT_PUBLIC_PLAYER_URL}?logs=false&source=add_screen`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-blue-400"
        >
          player.signagestudio.io
          <MdOpenInNew />
        </Link>
      </div>
      {" and enter the pairing code"}
    </div>,
  ],
  android: [
    <TutorialPage
      key="android_0"
      step={`From the Android TV Home screen, scroll to "Apps." Select the Google Play Store app.`}
      imageUrl="https://i0.wp.com/www.alphr.com/wp-content/uploads/2021/11/Android-TV-How-to-Download-Apps-From-the-App-Store-2.png?resize=768%2C432&ssl=1"
    />,
    <TutorialPage
      key="android_1"
      step={
        <div className="flex items-center gap-1">
          <MdSearch />
          <p>Search for</p>
          <p className="font-bold">Signage Studio</p>
          <p>and download it.</p>
        </div>
      }
      imageUrl="/dist/logo.svg"
    />,
  ],
  lg: [
    <TutorialPage
      key="lg_0"
      step="Press the Home button on the remote until a menu appears at the bottom of your screen."
      imageUrl="https://www.digitaltrends.com/wp-content/uploads/2022/11/gxca.jpg?fit=720%2C720&p=1"
    />,
    <TutorialPage
      key="lg_1"
      step="Select the LG Content Store option. It typically appears on the left side of the menu. Open the Apps option at the top of your screen."
      imageUrl="https://www.digitaltrends.com/wp-content/uploads/2022/11/trgdf.jpg?fit=720%2C720&p=1"
    />,
    <TutorialPage
      key="lg_2"
      step={
        <div className="flex items-center gap-1">
          <MdSearch />
          <p>Search for</p>
          <p className="font-bold">Signage Studio</p>
          <p>and download it.</p>
        </div>
      }
      imageUrl="/dist/logo.svg"
    />,
    <div
      key="lg_3"
      className="flex h-full min-w-full flex-col items-center justify-center gap-2 text-center text-sm text-gray-600 dark:text-gray-300"
    >
      <p className="font-semibold">Turn Off Screensaver</p>
      <CustomIcon>
        <PiTelevisionSimple />
      </CustomIcon>
      {
        "You can turn off the screen saver by pressing the gear button on the remote control."
      }

      <p>{"All Settings > General > Standby Mode > OFF."}</p>
    </div>,
  ],
  apple: [
    <div
      key="apple_0"
      className="flex h-full min-w-full flex-col items-center justify-center gap-2"
    >
      <CustomIcon>
        <SiAppletv />
      </CustomIcon>
      Apple
    </div>,
    <div
      key="apple_1"
      className="flex h-full min-w-full flex-col items-center justify-center gap-2"
    >
      <CustomIcon>
        <SiAppletv />
      </CustomIcon>
      Apple instruction 2
    </div>,
  ],
  fire: [
    <TutorialPage
      key="fire_0"
      step={`Select the Find button on your Fire TV home screen.`}
      imageUrl="https://b2024479.smushcdn.com/2024479/wp-content/uploads/2022/01/how-to-download-apps-on-fire-tv-stick_1-min-scaled.jpg?lossy=1&strip=1&webp=1"
    />,
    <TutorialPage
      key="fire_1"
      step={`Then select Search.`}
      imageUrl="https://b2024479.smushcdn.com/2024479/wp-content/uploads/2022/01/how-to-download-apps-on-fire-tv-stick_2-min-scaled.jpg?lossy=1&strip=1&webp=1"
    />,
    <TutorialPage
      key="fire_2"
      step={
        <div className="flex items-center gap-1">
          <MdSearch />
          <p>Search for</p>
          <p className="font-bold">Signage Studio</p>
          <p>and download it.</p>
        </div>
      }
      imageUrl="/dist/logo.svg"
    />,
  ],
  samsung: [
    <div
      key="samsung_0"
      className="flex h-full min-w-full flex-col items-center justify-center gap-2"
    >
      <CustomIcon>
        <SiSamsung />
      </CustomIcon>
      Open Samsung Store in your TV
    </div>,
    <div
      key="samsung_1"
      className="flex h-full min-w-full flex-col items-center justify-center gap-2"
    >
      <CustomIcon>
        <MdSearch />
      </CustomIcon>
      Search for <span className="font-bold">Signage Studio</span>
    </div>,

    <div
      key="samsung_1"
      className="flex h-full min-w-full flex-col items-center justify-center gap-2"
    >
      <CustomIcon>
        <MdDownload />
      </CustomIcon>
      Download the app
    </div>,
  ],
};
function CustomIcon({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-full bg-accentColor bg-opacity-5 p-6 text-6xl text-accentColor dark:bg-darkAccentColor dark:bg-opacity-5 dark:text-darkAccentColor">
      {children}
    </div>
  );
}

function TutorialPage({
  step,
  imageUrl,
}: {
  step: React.ReactNode;
  imageUrl?: string;
}) {
  return (
    <div className="flex h-full min-w-full flex-col items-center justify-center gap-4 text-sm text-gray-600 dark:text-gray-300">
      <p className="text-center">{step}</p>
      {imageUrl && (
        <div className="h-full flex-grow">
          <img
            src={imageUrl}
            alt="Step"
            className="h-full max-h-56 rounded-lg"
          />
        </div>
      )}
    </div>
  );
}

export const screenTypes: ScreenType[] = [
  {
    id: "apple",
    title: "Apple TV",
    icon: <SiAppletv />,
  },
  {
    id: "android",
    title: "Android TV",
    icon: <FaAndroid />,
  },
  {
    id: "samsung",
    title: "Samsung TV",
    icon: <SiSamsung />,
  },
  {
    id: "lg",
    title: "LG TV",
    icon: <LGLogo />,
  },
  {
    id: "fire",
    title: "Fire TV",
    icon: <FaAmazon />,
  },
  {
    id: "web",
    title: "Web",
    icon: <FaHtml5 />,
  },
];
export interface ScreenType {
  id: ScreenIdType;
  title: string;
  icon: JSX.Element;
}

export type ScreenIdType =
  | "android"
  | "apple"
  | "samsung"
  | "lg"
  | "fire"
  | "web"
  | "webBrowser";

function LGLogo() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      fill="none"
      viewBox="0 0 78 78"
    >
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M77.3 38.628c0 21.34-17.322 38.61-38.65 38.61C17.29 77.239 0 59.969 0 38.629S17.29 0 38.65 0C59.978 0 77.3 17.287 77.3 38.628ZM37.095 55.563V21.676H40.2v30.84h7.668v3.047H37.095ZM26.308 30.922a4.632 4.632 0 0 0 4.629-4.629c0-2.547-2.066-4.64-4.63-4.64-2.562 0-4.628 2.093-4.628 4.64a4.63 4.63 0 0 0 4.629 4.629ZM38.66 6.259c.367 0 1.135.025 1.539.045v3.01c-.379-.02-1.201-.02-1.54-.02-7.845 0-15.202 3.052-20.74 8.582-5.554 5.535-8.615 12.924-8.615 20.735 0 7.856 3.061 15.207 8.616 20.725 5.537 5.541 12.894 8.592 20.74 8.592 15.5 0 28.23-12.072 29.248-27.314.009-.052.012-.2.013-.316l.001-.147H47.898v-2.978h23.106v1.439c0 8.66-3.381 16.777-9.491 22.88-6.109 6.091-14.228 9.456-22.853 9.456-8.658 0-16.779-3.365-22.899-9.455-6.095-6.104-9.46-14.221-9.46-22.881 0-17.83 14.52-32.353 32.359-32.353Z"
        clipRule="evenodd"
      />
    </svg>
  );
}
